import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, query, serverTimestamp, updateDoc, where,
} from 'firebase/firestore';
import { Camera, Plus, ChevronLeft, Trash2, AlertTriangle } from 'lucide-react';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/PageHeader';
import { PhotoCapture, type CapturedPhoto } from '../components/PhotoCapture';
import { HOUR_FIELDS, blankEntry, computeTotals, extractLogbook, validateEntry } from '../lib/logbook';
import type { LogbookEntryDoc } from '../lib/types';

import { TRACKED_REG } from '../lib/flightTrack';
const num = (v: unknown) => (typeof v === 'number' && isFinite(v) ? v : 0);
const hr = (v: unknown) => (num(v) ? num(v).toFixed(1) : '');
const stripB64 = (dataUrl: string) => dataUrl.split(',')[1] ?? '';

// Phones produce huge HEIC/JPEG photos that blow past the API payload limit (and
// HEIC isn't always accepted). Redraw to a capped-size JPEG before sending — keeps
// the page legible for OCR while staying small + normalized.
function downscaleToJpeg(dataUrl: string, max = 1500, quality = 0.8): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const longest = Math.max(width, height);
      if (longest > max) {
        const s = max / longest;
        width = Math.round(width * s);
        height = Math.round(height * s);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Could not process the image.'));
      ctx.drawImage(img, 0, 0, width, height);
      try {
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch (e) {
        reject(e instanceof Error ? e : new Error('Could not process the image.'));
      }
    };
    img.onerror = () => reject(new Error('Could not read that photo — try retaking it.'));
    img.src = dataUrl;
  });
}

// Swipe a card left to reveal a Delete action (iOS-style); tap (no drag) opens it.
function SwipeRow({ onOpen, onDelete, children }: { onOpen: () => void; onDelete: () => void; children: ReactNode }) {
  const REVEAL = 88;
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const open = useRef(false);
  const startX = useRef(0);
  const moved = useRef(false);

  const onStart = (x: number) => { startX.current = x; moved.current = false; setDragging(true); };
  const onMove = (x: number) => {
    const raw = x - startX.current + (open.current ? -REVEAL : 0);
    if (Math.abs(x - startX.current) > 6) moved.current = true;
    setDx(Math.max(-REVEAL - 16, Math.min(0, raw)));
  };
  const onEnd = () => {
    setDragging(false);
    const shouldOpen = dx < -REVEAL / 2;
    open.current = shouldOpen;
    setDx(shouldOpen ? -REVEAL : 0);
  };
  const onClickRow = () => {
    if (open.current) { open.current = false; setDx(0); return; }
    if (!moved.current) onOpen();
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-slate-900/[0.06]">
      <button
        type="button"
        onClick={onDelete}
        aria-label="Delete entry"
        className="absolute inset-y-0 right-0 flex w-[88px] flex-col items-center justify-center gap-1 bg-red-600 text-xs font-semibold text-white"
      >
        <Trash2 size={18} /> Delete
      </button>
      <div
        onTouchStart={(e) => onStart(e.touches[0].clientX)}
        onTouchMove={(e) => onMove(e.touches[0].clientX)}
        onTouchEnd={onEnd}
        onClick={onClickRow}
        style={{
          transform: `translateX(${dx}px)`,
          transition: dragging ? 'none' : 'transform .2s ease',
          touchAction: 'pan-y',
          WebkitUserSelect: 'none',
          userSelect: 'none',
        }}
        className="relative w-full bg-white p-0 text-left"
      >
        {children}
      </div>
    </div>
  );
}

export function Logbook() {
  const { profile, fbUser, demoMode } = useAuth();
  const uid = profile?.uid || fbUser?.uid || '';
  const [entries, setEntries] = useState<LogbookEntryDoc[]>([]);
  const [view, setView] = useState<'digital' | 'traditional'>('digital');
  const [editing, setEditing] = useState<Partial<LogbookEntryDoc> | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [hex, setHex] = useState('#E7EFE0');
  const [op, setOp] = useState(100);

  useEffect(() => {
    if (demoMode || !db || !uid) return;
    const q = query(collection(db, 'logbookEntries'), where('ownerUid', '==', uid));
    const unsub = onSnapshot(q, (snap) =>
      setEntries(snap.docs.map((d) => ({ ...(d.data() as LogbookEntryDoc), entryId: d.id })))
    );
    return () => unsub();
  }, [demoMode, uid]);

  const sorted = useMemo(
    () => [...entries].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)),
    [entries]
  );
  const totals = useMemo(() => computeTotals(sorted), [sorted]);

  async function saveEntry(e: Partial<LogbookEntryDoc>) {
    const v = validateEntry(e);
    const payload = { ...e, ownerUid: uid, needsReview: v.needsReview, reviewNote: v.reviewNote, updatedAt: serverTimestamp() };
    delete (payload as { entryId?: string }).entryId;
    if (!db) return;
    if (e.entryId) await updateDoc(doc(db, 'logbookEntries', e.entryId), payload);
    else await addDoc(collection(db, 'logbookEntries'), { ...payload, source: e.source || 'manual', createdAt: serverTimestamp() });
    setEditing(null);
  }
  async function removeEntry(id: string) {
    if (db && id) await deleteDoc(doc(db, 'logbookEntries', id));
    setEditing(null);
  }
  async function saveMany(rows: Partial<LogbookEntryDoc>[]) {
    if (!db) return;
    for (const r of rows) {
      const v = validateEntry(r);
      await addDoc(collection(db, 'logbookEntries'), {
        ...r, ownerUid: uid, source: 'photo', needsReview: v.needsReview, reviewNote: v.reviewNote, createdAt: serverTimestamp(),
      });
    }
    setCapturing(false);
  }

  const reviewCount = sorted.filter((e) => e.needsReview).length;

  return (
    <>
      <PageHeader
        title="Logbook"
        subtitle="Your flights — digital and traditional views."
        actions={
          <div className="flex gap-2">
            <button onClick={() => setCapturing(true)} className="btn-secondary py-1.5 text-sm flex items-center gap-1.5">
              <Camera size={15} /> Capture
            </button>
            <button onClick={() => setEditing(blankEntry(uid))} className="btn-primary py-1.5 text-sm flex items-center gap-1.5">
              <Plus size={15} /> Add
            </button>
          </div>
        }
      />

      {/* totals */}
      <div className="card mb-4 flex gap-0 overflow-x-auto p-0">
        {[
          ['Total', totals.total], ['PIC', totals.pic], ['X-C', totals.crossCountry],
          ['Night', totals.night], ['Instr', totals.instrument], ['Landings', totals.landings],
        ].map(([k, v]) => (
          <div key={k as string} className="min-w-[92px] flex-shrink-0 border-r border-slate-100 px-4 py-3 last:border-0">
            <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{k}</div>
            <div className="mt-0.5 text-xl font-bold tracking-tight">{k === 'Landings' ? num(v) : num(v).toFixed(1)}</div>
          </div>
        ))}
      </div>

      {/* toggle */}
      <div className="mb-3 flex gap-2">
        <button onClick={() => setView('digital')} className={view === 'digital' ? 'btn-primary py-1.5 text-sm flex-1' : 'btn-secondary py-1.5 text-sm flex-1'}>Digital</button>
        <button onClick={() => setView('traditional')} className={view === 'traditional' ? 'btn-primary py-1.5 text-sm flex-1' : 'btn-secondary py-1.5 text-sm flex-1'}>Logbook</button>
      </div>

      {reviewCount > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{reviewCount} entr{reviewCount === 1 ? 'y needs' : 'ies need'} review — tap to fix the flagged numbers.</span>
        </div>
      )}

      {sorted.length === 0 ? (
        <div className="card p-8 text-center text-sm text-ink-muted">
          No flights yet. Tap <strong>Capture</strong> to digitize a paper logbook page, or <strong>Add</strong> to enter one.
        </div>
      ) : view === 'digital' ? (
        <div className="space-y-3">
          {sorted.map((e) => (
            <SwipeRow key={e.entryId} onOpen={() => setEditing(e)} onDelete={() => removeEntry(e.entryId)}>
              <div className="flex items-start justify-between px-4 pt-3.5">
                <div>
                  <div className="font-bold tracking-tight">{e.date}</div>
                  <div className="text-[13px] text-ink-muted">{[e.aircraftType, e.ident].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-extrabold tracking-tight">{hr(e.total) || '—'}</div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle">total</div>
                </div>
              </div>
              {(e.from || e.to) && (
                <div className="px-4 pb-1 pt-1 font-semibold text-navy-700">
                  {e.from || '—'} <span className="text-ink-subtle">→</span> {e.to || '—'}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 px-4 pb-3 pt-1">
                {num(e.pic) > 0 && <span className="badge-success">PIC {hr(e.pic)}</span>}
                {num(e.dualReceived) > 0 && <span className="badge-info">Dual {hr(e.dualReceived)}</span>}
                {num(e.crossCountry) > 0 && <span className="badge-neutral">X-C {hr(e.crossCountry)}</span>}
                {num(e.night) > 0 && <span className="badge-neutral">Night {hr(e.night)}</span>}
                {num(e.actualInstrument) + num(e.simInstrument) > 0 && <span className="badge-info">IFR {hr(num(e.actualInstrument) + num(e.simInstrument))}</span>}
                {(num(e.dayLandings) + num(e.nightLandings)) > 0 && <span className="badge-neutral">{num(e.dayLandings) + num(e.nightLandings)} ldg</span>}
                {e.needsReview && <span className="badge-warning">Review</span>}
              </div>
              {e.remarks && <div className="border-t border-slate-100 px-4 py-2.5 text-[13px] text-ink-muted">{e.remarks}</div>}
            </SwipeRow>
          ))}
        </div>
      ) : (
        <TraditionalView entries={sorted} hex={hex} op={op} setHex={setHex} setOp={setOp} onTap={setEditing} />
      )}

      {editing && (
        <EntryEditor entry={editing} onClose={() => setEditing(null)} onSave={saveEntry} onDelete={removeEntry} />
      )}
      {capturing && <CaptureModal onClose={() => setCapturing(false)} onSave={saveMany} />}
    </>
  );
}

function TraditionalView({
  entries, hex, op, setHex, setOp, onTap,
}: {
  entries: LogbookEntryDoc[]; hex: string; op: number; setHex: (v: string) => void; setOp: (v: number) => void;
  onTap: (e: LogbookEntryDoc) => void;
}) {
  let r = 0, g = 0, b = 0;
  try { const h = hex.replace('#', ''); r = parseInt(h.slice(0, 2), 16); g = parseInt(h.slice(2, 4), 16); b = parseInt(h.slice(4, 6), 16); } catch { /* */ }
  const paper = `rgba(${r},${g},${b},${op / 100})`;
  const T = computeTotals(entries);
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 px-3 py-2">
        <span className="h-7 w-7 rounded-lg border border-slate-300" style={{ background: hex }} />
        <input value={hex} onChange={(e) => setHex(e.target.value)} className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 font-mono text-xs" />
        <span className="text-xs text-ink-muted">Opacity</span>
        <input type="range" min={20} max={100} value={op} onChange={(e) => setOp(Number(e.target.value))} className="flex-1" />
      </div>
      <div className="overflow-x-auto rounded-xl bg-slate-100 p-3">
        <table className="min-w-[820px] border-collapse text-xs" style={{ background: paper, borderRadius: 4 }}>
          <thead>
            <tr className="[&>th]:border [&>th]:border-[#bcd0b4] [&>th]:px-1.5 [&>th]:py-1.5 [&>th]:text-[9px] [&>th]:font-extrabold [&>th]:uppercase [&>th]:text-[#33402c]">
              <th>Date</th><th>Type</th><th>Ident</th><th>From</th><th>To</th><th>Ldg</th><th>Total</th><th>PIC</th><th>Dual</th><th>Solo</th><th>X-C</th><th>Night</th><th>Instr</th><th>Remarks</th>
            </tr>
          </thead>
          <tbody className="[&_td]:h-8 [&_td]:border [&_td]:border-[#bcd0b4] [&_td]:px-1.5 [&_td]:py-1.5 [&_td]:text-[#243018]">
            {entries.map((e) => (
              <tr key={e.entryId} onClick={() => onTap(e)} className="cursor-pointer">
                <td>{e.date?.slice(5)}</td><td>{e.aircraftType}</td><td>{e.ident}</td><td>{e.from}</td><td>{e.to}</td>
                <td className="text-right">{num(e.dayLandings) + num(e.nightLandings) || ''}</td>
                <td className="text-right">{hr(e.total)}</td><td className="text-right">{hr(e.pic)}</td><td className="text-right">{hr(e.dualReceived)}</td>
                <td className="text-right">{hr(e.solo)}</td><td className="text-right">{hr(e.crossCountry)}</td><td className="text-right">{hr(e.night)}</td>
                <td className="text-right">{hr(num(e.actualInstrument) + num(e.simInstrument))}</td>
                <td className="max-w-[160px] truncate">{e.remarks}</td>
              </tr>
            ))}
            <tr className="[&>td]:border [&>td]:border-[#bcd0b4] [&>td]:px-1.5 [&>td]:py-1.5 [&>td]:font-extrabold">
              <td colSpan={5}>Totals</td>
              <td className="text-right">{T.landings || ''}</td>
              <td className="text-right">{T.total.toFixed(1)}</td><td className="text-right">{T.pic.toFixed(1)}</td>
              <td className="text-right">{T.dualReceived.toFixed(1)}</td><td className="text-right">{T.solo.toFixed(1)}</td>
              <td className="text-right">{T.crossCountry.toFixed(1)}</td><td className="text-right">{T.night.toFixed(1)}</td>
              <td className="text-right">{T.instrument.toFixed(1)}</td><td />
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-center text-[11px] text-ink-subtle">Traditional spread · page color customizable · tap a row to edit.</p>
    </div>
  );
}

// ───────── entry editor (add / edit / review) — back button, scrollable, save footer ─────────
const TEXT_FIELDS: { key: keyof LogbookEntryDoc; label: string; ph?: string }[] = [
  { key: 'date', label: 'Date' }, { key: 'aircraftType', label: 'Aircraft type', ph: 'C-172' },
  { key: 'ident', label: 'Aircraft ident', ph: TRACKED_REG }, { key: 'from', label: 'From', ph: 'KLGB' }, { key: 'to', label: 'To', ph: 'KSMO' },
];
const COUNT_FIELDS: { key: keyof LogbookEntryDoc; label: string }[] = [
  { key: 'dayLandings', label: 'Day ldg' }, { key: 'nightLandings', label: 'Night ldg' },
  { key: 'takeoffs', label: 'Takeoffs' }, { key: 'approaches', label: 'Appr.' },
];

function EntryEditor({
  entry, onClose, onSave, onDelete,
}: {
  entry: Partial<LogbookEntryDoc>;
  onClose: () => void;
  onSave: (e: Partial<LogbookEntryDoc>) => void;
  onDelete: (id: string) => void;
}) {
  const [e, setE] = useState<Partial<LogbookEntryDoc>>({ ...entry });
  const set = (k: keyof LogbookEntryDoc, v: string) =>
    setE((p) => ({ ...p, [k]: v }));
  const setN = (k: keyof LogbookEntryDoc, v: string) =>
    setE((p) => ({ ...p, [k]: v === '' ? undefined : Number(v) }));
  const v = validateEntry(e);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-canvas">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-3 py-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <button onClick={onClose} className="flex items-center gap-1 text-sky-600"><ChevronLeft size={20} /> Back</button>
        <span className="font-display font-semibold text-navy-900">{entry.entryId ? 'Edit flight' : 'New flight'}</span>
        {entry.entryId ? (
          <button onClick={() => onDelete(entry.entryId as string)} className="p-1.5 text-red-600"><Trash2 size={18} /></button>
        ) : <span className="w-8" />}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
        {v.needsReview && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>Check this: {v.reviewNote}.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          {TEXT_FIELDS.map((f) => (
            <div key={f.key} className={f.key === 'date' ? 'col-span-2' : ''}>
              <label className="label">{f.label}</label>
              <input
                className="input"
                type={f.key === 'date' ? 'date' : 'text'}
                placeholder={f.ph}
                value={(e[f.key] as string) ?? ''}
                onChange={(ev) => set(f.key, ev.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="mt-5 mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Counts</div>
        <div className="grid grid-cols-4 gap-2">
          {COUNT_FIELDS.map((f) => (
            <div key={f.key}><label className="label">{f.label}</label>
              <input className="input" type="number" inputMode="numeric" value={(e[f.key] as number) ?? ''} onChange={(ev) => setN(f.key, ev.target.value)} /></div>
          ))}
        </div>

        <div className="mt-5 mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Total duration</div>
        <input className="input" type="number" inputMode="decimal" step="0.1" placeholder="0.0" value={(e.total as number) ?? ''} onChange={(ev) => setN('total', ev.target.value)} />

        <div className="mt-5 mb-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted">Hours</div>
        <div className="grid grid-cols-2 gap-3">
          {HOUR_FIELDS.map((f) => (
            <div key={f.key}><label className="label">{f.label}</label>
              <input className="input" type="number" inputMode="decimal" step="0.1" value={(e[f.key] as number) ?? ''} onChange={(ev) => setN(f.key, ev.target.value)} /></div>
          ))}
        </div>

        <div className="mt-5"><label className="label">Remarks &amp; endorsements</label>
          <textarea className="input-textarea" value={(e.remarks as string) ?? ''} onChange={(ev) => set('remarks', ev.target.value)} /></div>
      </div>

      <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <button onClick={() => onSave(e)} className="btn-primary w-full">Save flight</button>
      </div>
    </div>
  );
}

// ───────── capture: photograph paper logbook → AI read → review → save ─────────
function CaptureModal({ onClose, onSave }: { onClose: () => void; onSave: (rows: Partial<LogbookEntryDoc>[]) => void }) {
  const [left, setLeft] = useState<CapturedPhoto | null>(null);
  const [right, setRight] = useState<CapturedPhoto | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<Partial<LogbookEntryDoc>[] | null>(null);
  const [pageTotal, setPageTotal] = useState<number | null>(null);

  async function read() {
    if (!left || !right) return;
    setBusy(true); setErr(null);
    try {
      const imgs = [
        stripB64(await downscaleToJpeg(left.dataUrl)),
        stripB64(await downscaleToJpeg(right.dataUrl)),
      ];
      const out = await extractLogbook(imgs, 'image/jpeg');
      setRows(out.entries);
      setPageTotal(out.pageTotal);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not read the logbook.');
    } finally {
      setBusy(false);
    }
  }
  const flagged = rows ? rows.filter((r) => validateEntry(r).needsReview).length : 0;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-canvas">
      <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-3 py-3" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
        <button onClick={onClose} className="flex items-center gap-1 text-sky-600"><ChevronLeft size={20} /> Back</button>
        <span className="font-display font-semibold text-navy-900">Capture logbook</span><span className="w-12" />
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4">
        {!rows ? (
          <>
            <p className="mb-3 text-sm text-ink-muted">
              A logbook is a two-page spread — one flight spans both pages. Shoot the <strong>left</strong> page, then the <strong>right</strong> page of the same spread. Both are required.
            </p>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">Left page</div>
            <PhotoCapture subject="the left page of your logbook" busy={busy} onChange={setLeft} />
            <div className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">Right page</div>
            <PhotoCapture subject="the right page of the same spread" busy={busy} onChange={setRight} />
          </>
        ) : (
          <>
            {(() => {
              if (pageTotal == null) return null;
              const sum = rows.reduce((s, r) => s + num(r.total), 0);
              const diff = Math.round((pageTotal - sum) * 10) / 10;
              if (Math.abs(diff) < 0.1)
                return (
                  <div className="mb-3 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
                    <span>✅</span>
                    <span>Totals reconcile — {sum.toFixed(1)} hrs captured matches the page total ({pageTotal.toFixed(1)}).</span>
                  </div>
                );
              return (
                <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-800">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>
                    <strong>Totals don’t match the page.</strong> Captured {sum.toFixed(1)} hrs but the page total reads {pageTotal.toFixed(1)} —{' '}
                    {diff > 0
                      ? `${diff.toFixed(1)} hrs missing (a flight was likely dropped or misread)`
                      : `${Math.abs(diff).toFixed(1)} hrs over (a flight may be duplicated)`}
                    . Re-shoot or fix before adding.
                  </span>
                </div>
              );
            })()}
            <p className="mb-3 text-sm">
              Read <strong>{rows.length}</strong> flight{rows.length === 1 ? '' : 's'}{flagged ? ` · ${flagged} need review` : ''}. Add them, then tap any flight to fine-tune.
            </p>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="card p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="font-semibold">{r.date || '—'} · {r.aircraftType || ''} {r.ident || ''}</span>
                    <span className="font-bold">{hr(r.total) || '—'}</span>
                  </div>
                  <div className="text-ink-muted">{r.from || '—'} → {r.to || '—'}{r.remarks ? ` · ${r.remarks}` : ''}</div>
                  {validateEntry(r).needsReview && <div className="mt-1 text-xs text-amber-700">⚠️ {validateEntry(r).reviewNote}</div>}
                </div>
              ))}
            </div>
          </>
        )}
        {err && <p className="mt-3 text-sm text-red-700">{err}</p>}
      </div>
      <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        {!rows ? (
          <button onClick={read} disabled={!left || !right || busy} className="btn-primary w-full">{busy ? 'Reading carefully… (up to 2 min)' : !left || !right ? 'Add both pages to continue' : 'Read logbook'}</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => setRows(null)} className="btn-secondary flex-1">Re-shoot</button>
            <button onClick={() => onSave(rows)} className="btn-primary flex-1">Add {rows.length} to logbook</button>
          </div>
        )}
      </div>
    </div>
  );
}
